import React, { useState, useEffect } from "react";
import CompanyLayout from "../../components/layouts/CompanyLayout";
import { doc, getDoc } from "firebase/firestore";
import { auth, firestore } from "../../firebaseConfig";
import ClientTable from "../company/components/ClientTable";
import ShopTable from "../company/components/ShopTable";
import PendingClientRequests from "../company/components/PendingClientRequests";
import ClientOrdersTab from "../company/components/ClientOrdersTab";
import ClientTransactionsTab from "../company/components/ClientTransactionsTab";
import MilkSoldTab from "../company/components/MilkSoldTab";
import AddClientModal from "../company/components/AddClientModal";
import CompanyInvoicesTab from "../company/components/CompanyInvoicesTab";
import MessagingPanel from "../../components/messaging/MessagingPanel";
import ThreadList from "../../components/messaging/ThreadList";
import ChatModal from "../../components/messaging/ChatModal";
import { COMPANY_CLIENT_TABS } from "./companyClientsTabs";

export const ClientReportsInsightsPlaceholder = () => (
  <div className="bg-white rounded-lg shadow p-6">
    <h2 className="text-xl font-semibold text-gray-800">Reports & Insights</h2>
  </div>
);

const CompanyClient = () => {
  const [activeTab, setActiveTab] = useState("clients");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [messageThreadId, setMessageThreadId] = useState("");
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);

  // 🔹 Fetch company name
  useEffect(() => {
    const fetchCompanyName = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        const cached = localStorage.getItem(`companyName_${user.uid}`);
        if (cached) {
          setCompanyName(cached);
          setLoadingCompany(false);
          return;
        }

        const userDoc = await getDoc(doc(firestore, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.companyName) {
            setCompanyName(data.companyName);
            localStorage.setItem(`companyName_${user.uid}`, data.companyName);
          }
        }
      } catch (error) {
        console.error("Error fetching company name:", error);
      } finally {
        setLoadingCompany(false);
      }
    };

    fetchCompanyName();
  }, []);

  return (
    <CompanyLayout>
      <div className="p-6 text-gray-800 transition-all duration-300 overflow-y-auto max-h-[calc(100vh-6rem)]">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-cyan-700 flex items-center gap-2">
            🧾 Client Management
          </h1>

          <button
            onClick={() => setIsAddModalOpen(true)}
            disabled={loadingCompany || !companyName}
            className={`${
              loadingCompany
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-cyan-600 hover:bg-cyan-700"
            } text-white px-4 py-2 rounded-lg shadow-md transition-transform hover:scale-105`}
          >
            + Add Client
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 border-b border-gray-200 mb-4">
          {COMPANY_CLIENT_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-2 px-4 font-medium transition-all duration-200 flex items-center gap-2 ${
                activeTab === tab.id
              ? "text-cyan-700 border-b-2 border-cyan-700"
              : "text-gray-500 hover:text-cyan-600"
            }`}
          >
            <span aria-hidden="true">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

        {/* Tab Content */}
        <div className="transition-all duration-500 ease-in-out" key={activeTab}>
          {activeTab === "clients" && <ClientTable companyName={companyName} />}
          {activeTab === "shops" && <ShopTable companyName={companyName} />}
          {activeTab === "orders" && <ClientOrdersTab companyName={companyName} />}
          {activeTab === "requests" && (
            <PendingClientRequests companyName={companyName} />
          )}
          {activeTab === "payments" && <ClientTransactionsTab companyName={companyName} />}
          {activeTab === "milkSold" && <MilkSoldTab companyName={companyName} />}
          {activeTab === "analytics" && <ClientReportsInsightsPlaceholder />}
          {activeTab === "invoices" && <CompanyInvoicesTab companyName={companyName} />}
          {activeTab === "messages" && (
            <div className="bg-white rounded-lg shadow p-6 space-y-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Client Messages</h2>
                <p className="text-gray-600 text-sm">
                  Client chats land here. AI monitored for violations.
                </p>
              </div>
              <div className="flex flex-col lg:flex-row gap-4 items-start">
                <ThreadList
                  companyName={companyName}
                  participantType="client"
                  selectedParticipantId={messageThreadId}
                  onSelect={(id) => {
                    setMessageThreadId(id);
                    setIsChatModalOpen(true);
                  }}
                />
                <div className="flex-1 w-full">
                  <div className="border border-dashed border-slate-200 rounded-lg p-4 text-sm text-slate-600">
                    Pick a conversation from the list to reply. New client messages open in a chat modal so you can scroll and respond quickly.
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeTab === "approvals" && (
            <div className="bg-white rounded-lg shadow p-6 space-y-2">
              <h2 className="text-xl font-semibold text-gray-800">Client Approvals</h2>
              <p className="text-gray-600">
                Company Admins and Managers can review and approve client-related edit or delete requests before they take effect.
              </p>
              <p className="text-sm text-gray-500">
                Future: integrate dedicated approval workflows for client updates and removals.
              </p>
            </div>
          )}
        </div>

        {/* Add Client Modal */}
        {isAddModalOpen && (
          <AddClientModal
            open={isAddModalOpen}
            onClose={() => setIsAddModalOpen(false)}
            companyName={companyName}
          />
        )}
        <ChatModal
          open={isChatModalOpen && !!messageThreadId}
          onClose={() => setIsChatModalOpen(false)}
          title={`Chat with ${messageThreadId || "client"}`}
          messagingProps={{
            companyName,
            participantId: messageThreadId,
            actorType: "company",
            title: "Company ↔ Client chat",
            onConversationDeleted: () => setMessageThreadId(""),
          }}
        />
      </div>
    </CompanyLayout>
  );
};

export default CompanyClient;
